import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import { CameraTile } from '../components/CameraTile';
import { mintaLayarPenuh } from '../lib/layarPenuh';
import { FullscreenViewer } from '../components/FullscreenViewer';
import { NomorHalaman } from '../components/Pager';
import { TombolIkon } from '../components/IconAction';
import { cocokKamera } from '../lib/cariKamera';
import { useBahasa } from '../i18n';
import { tunjukKamera } from '../lib/prewarm';
import { useGeserJari } from '../lib/geserJari';
import { usePanahTv, type ArahPanah } from '../lib/panahTv';
import { api, gridDim, gridSel, GRID_LAYOUTS, type Camera, type GridLayout } from '../lib/api';
import { bawaanUntuk, pilihanDengan, pilihanUntuk, sahUntuk } from '../lib/gridFit';
import { isStaff, useAuth } from '../hooks/useAuth';
import { WallStatus } from '../components/WallStatus';
import { useLayarLebar } from '../hooks/useLayarLebar';

/** Interval slideshow §C1, dalam detik. Default 60 (§G8.1). */
const INTERVALS = [
  { s: 15, label: '15 seconds' },
  { s: 30, label: '30 seconds' },
  { s: 45, label: '45 seconds' },
  { s: 60, label: '1 minute' },
  { s: 120, label: '2 minutes' },
  { s: 180, label: '3 minutes' },
  { s: 300, label: '5 minutes' },
];

/** Status kamera di-poll, bukan realtime — tidak sepadan biayanya (§G9). */
const STATUS_POLL_MS = 30_000;

/**
 * Berapa lama pemilih tata letak bertahan sesudah sentuhan terakhir (§G8.2).
 *
 * Empat detik: cukup untuk membaca enam tombol dan menekan salah satunya tanpa
 * terburu-buru, tapi tidak cukup lama untuk tertinggal menyala di TV yang
 * kebetulan tersenggol.
 */
const KENDALI_TIDUR_MS = 4_000;

/** Nama tersimpan → pilihan yang dikenal. Yang asing diperlakukan seperti tidak ada. */
function layoutFromName(name: string | null | undefined): GridLayout | null {
  return GRID_LAYOUTS.includes(name as GridLayout) ? (name as GridLayout) : null;
}

export function DashboardPage() {
  const { user, savePrefs } = useAuth();
  const { t } = useBahasa();
  /**
   * Di bawah 1024px Wall Status pindah ke baris hamburger `AppHeader` (§G14,
   * revisi 1 September 2026) — jadi di sana halaman ini tidak memasangnya sama
   * sekali, bukan sekadar menyembunyikannya: dua salinan berarti dua poll.
   */
  const lebar = useLayarLebar();
  /**
   * `?monitor=<id>` mempersempit halaman ini ke satu custom monitor (§F9).
   *
   * Monitor tidak punya pemutarnya sendiri dengan sengaja: grid, slideshow,
   * layar penuh, paging, dan seluruh aturan §D3.2 sudah hidup di halaman ini.
   * Menyalinnya ke halaman kedua berarti dua tempat yang harus diperbaiki tiap
   * kali salah satunya berubah — dan yang kedua selalu ketinggalan.
   */
  const [params] = useSearchParams();
  const monitorId = params.get('monitor');
  const [monitor, setMonitor] = useState<{ name: string; cameraIds: string[] } | null>(null);

  const [cameras, setCameras] = useState<Camera[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(0);
  const [fullscreenId, setFullscreenId] = useState<string | null>(null);

  // Tinggi ikut disimpan karena §G8 sekarang membedakan potret dari landscape.
  const [viewport, setViewport] = useState(() => ({
    w: window.innerWidth,
    h: window.innerHeight,
  }));
  /**
   * Tata letak tersimpan menang atas default lebar layar (§G8).
   *
   * Diambil dari akun, bukan localStorage: satu PC pos satpam dipakai
   * bergantian, dan pilihan yang menempel ke browser akan saling menimpa.
   * Pilihan yang tidak muat di lebar layar sekarang tetap disaring efek di
   * bawah — 5×5 yang disimpan di TV tidak boleh ikut terbawa ke layar 390px.
   */
  const tersimpan = layoutFromName(user?.gridLayout);
  const [layout, setLayout] = useState<GridLayout>(
    () => tersimpan ?? bawaanUntuk(window.innerWidth, window.innerHeight),
  );

  /**
   * Kotak tempat grid benar-benar digambar — diukur, bukan ditebak dari
   * viewport (§G8, 23 Agu 2026).
   *
   * Yang menentukan tata letak mana yang muat adalah bentuk kotaknya, dan kotak
   * itu bukan layar: di dashboard ada bilah kendali di atasnya dan sidebar di
   * sampingnya, di slideshow tidak ada apa-apa. Menghitung dari `innerWidth`
   * berarti menganggap keduanya sama, padahal menciutkan sidebar saja sudah
   * cukup untuk memindahkan jawabannya dari satu tata letak ke tata letak lain.
   *
   * Nilai awalnya viewport supaya render pertama tidak menunggu pengukuran;
   * `ResizeObserver` menggantinya begitu elemennya ada.
   */
  const [kotak, setKotak] = useState(() => ({
    w: window.innerWidth,
    h: window.innerHeight,
  }));
  const pengamatRef = useRef<ResizeObserver | null>(null);
  const elGridRef = useRef<HTMLDivElement | null>(null);
  const sudahDiukur = useRef(false);
  /**
   * Cermin `kotak` supaya keputusan di pengamat tidak perlu diambil di dalam
   * updater `setState` — updater wajib murni, sedangkan yang di bawah membaca
   * sekaligus menulis ref.
   */
  const kotakRef = useRef({ w: window.innerWidth, h: window.innerHeight });

  /**
   * Terima pengukuran baru — dipakai pengamat DAN penangan `resize`.
   *
   * Dibulatkan supaya pergeseran sub-piksel saat menggulung tidak memicu render
   * ulang beruntun.
   */
  const terimaKotak = useCallback((w: number, h: number) => {
    sudahDiukur.current = true;
    kotakRef.current = { w, h };
    setKotak((k) => (k.w === w && k.h === h ? k : { w, h }));
  }, []);

  const gridRef = useCallback(
    (el: HTMLDivElement | null) => {
      pengamatRef.current?.disconnect();
      elGridRef.current = el;
      if (!el) return;
      const ro = new ResizeObserver(([entri]) => {
        const r = entri!.contentRect;
        const w = Math.round(r.width);
        const h = Math.round(r.height);

        /**
         * **Perubahan tinggi tanpa perubahan lebar diabaikan.** Ini bukan
         * penghematan render, ini yang menghentikan dashboard bergetar.
         *
         * Kotak ini yang menentukan `options`, `options` yang menentukan berapa
         * tombol tata letak dirender, dan tombol-tombol itu duduk di bilah
         * kendali **tepat di atas grid** — bilah yang `flex-wrap`. Satu tombol
         * lebih banyak bisa membuat bilahnya membungkus jadi satu baris lagi,
         * bilah yang lebih tinggi memendekkan grid, dan grid yang lebih pendek
         * menghasilkan jawaban `options` yang lain. Jawabannya mengubah
         * pertanyaannya.
         *
         * Untuk sebagian ukuran layar umpan balik itu tidak punya titik tetap
         * sama sekali. Terukur 23 Agu 2026 dengan menghitung peta k→k' (k tombol
         * menghasilkan k' tombol) untuk tiap ukuran: 2.042 pasangan lebar×tinggi
         * tidak punya satu pun k dengan k'=k. Contohnya lebar 368:
         * `{1→3, 2→3, 3→2, 4→2, 5→2, 6→2}` — tiga tombol menjawab dua, dua
         * tombol menjawab tiga, selamanya, secepat `ResizeObserver` dipanggil.
         * Itulah "bergetar"-nya: bilah dan grid berkedip 60×/detik.
         *
         * Yang memutus lingkarannya: **lebar** grid tidak pernah bisa diubah
         * oleh bilah kendali — membungkus hanya menambah baris, tidak menggeser
         * sisi kiri-kanan. Jadi lebar adalah satu-satunya sinyal yang benar-benar
         * datang dari luar (jendela diubah, sidebar diciutkan, laci monitor
         * dibuka), dan hanya itu yang boleh membuka pengukuran baru. Perubahan
         * tinggi murni datang dari bilah itu sendiri, dan menuruti tinggi itu
         * sama dengan menuruti jawaban kita sendiri.
         *
         * Tinggi jendela yang benar-benar berubah tetap tertangkap: penangan
         * `resize` di bawah membaca ulang kotaknya secara eksplisit.
         */
        if (sudahDiukur.current && kotakRef.current.w === w) return;
        terimaKotak(w, h);
      });
      ro.observe(el);
      pengamatRef.current = ro;
    },
    [terimaKotak],
  );

  /** Baca ulang kotak apa adanya — dipanggil saat jendela benar-benar berubah. */
  const bacaUlangKotak = useCallback(() => {
    const el = elGridRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    terimaKotak(Math.round(r.width), Math.round(r.height));
  }, [terimaKotak]);

  useEffect(() => () => pengamatRef.current?.disconnect(), []);
  // Sekali user memilih sendiri, tata letaknya tidak pernah diutak-atik lagi.
  // Pilihan yang datang dari akun juga dihitung sebagai pilihan user.
  const layoutDipilihUser = useRef(tersimpan !== null);

  const [slideshow, setSlideshow] = useState(false);
  /**
   * Slideshow sedang berjalan atau sedang dijeda (§G8.2).
   *
   * Terpisah dari `slideshow` yang menyatakan modenya terbuka: jeda berarti
   * "berhenti di kamera ini", bukan "kembali ke panel dashboard".
   */
  const [jalan, setJalan] = useState(true);
  /**
   * Layar penuh **manual** — dibedakan dari slideshow yang sedang dijeda
   * (§G8.2, direvisi 24 Agustus 2026).
   *
   * Sampai revisi ini keduanya satu keadaan: `slideshow && !jalan`. Yang
   * memaksa dipisah adalah permintaan lapangan supaya mode manual **tidak
   * punya tombol jeda/lanjut sama sekali** — kendalinya tinggal `‹` dan `›`.
   * Tanpa bendera sendiri, menekan ⏸ pada slideshow akan menghapus tombol yang
   * baru saja ditekan, dan tidak ada lagi jalan untuk melanjutkannya.
   */
  const [manual, setManual] = useState(false);
  const [interval, setIntervalS] = useState(60);
  const [countdown, setCountdown] = useState(60);

  /**
   * SELURUH kendali slideshow — pemilih tata letak dan kelompok transport —
   * muncul saat ada tanda kehidupan lalu hilang sendiri kalau didiamkan
   * (§G8.2, 23 Agu 2026).
   *
   * Sampai revisi ini yang menyembunyikan diri hanya pemilih tata letak;
   * transport (mundur · jeda/lanjut · maju) menetap dengan alasan "TV pos
   * satpam tidak punya penunjuk". Alasan itu tetap benar, tapi jawabannya
   * bukan kendali yang menetap selamanya: yang dibutuhkan TV tanpa penunjuk
   * adalah kendali yang **bisa dipanggil**, dan itu sekarang dijamin oleh
   * pemicu bangun yang lebih lebar di bawah (gerak penunjuk, sentuhan, dan
   * tombol papan ketik apa pun). Yang tersisa di layar setelah 4 detik adalah
   * gambar kamera — yang memang jadi alasan mode ini ada (§G7 no.1).
   *
   * Tombol "Stop (Esc)" di pojok kanan atas SENGAJA tidak ikut: ia jalan
   * keluar, bukan kendali pemutaran, dan jalan keluar yang harus dipanggil
   * dulu bukan jalan keluar.
   */
  const [kendaliTampak, setKendaliTampak] = useState(false);
  const grupGridRef = useRef<HTMLDivElement>(null);
  const grupTransportRef = useRef<HTMLDivElement>(null);
  const tidurRef = useRef<number | undefined>(undefined);

  /** Tampilkan kendali, lalu hitung mundur menuju hilang lagi. */
  const bangunkanKendali = useCallback(() => {
    setKendaliTampak(true);
    window.clearTimeout(tidurRef.current);
    tidurRef.current = window.setTimeout(function tidur() {
      /**
       * Jangan pernah melepas elemen yang sedang dipegang fokus **papan ketik**
       * — fokusnya akan terlempar ke <body> dan orangnya kehilangan tempatnya
       * di tengah memilih. Tunggu sampai fokusnya pindah.
       *
       * Yang menahan hanya fokus yang **terlihat** (`:focus-visible`), dan itu
       * perbaikan 25 Agustus 2026: klik tetikus pada sebuah <button> juga
       * meninggalkan fokus di sana, jadi penjaga ini dulu menunda tidurnya
       * selamanya begitu satu tombol pernah diklik. Di layar penuh manual itu
       * berarti kendalinya **tidak pernah** hilang — satu-satunya cara memakai
       * mode itu memang menekan `‹`/`›` — sementara slideshow yang berjalan
       * sendiri tanpa disentuh tetap tidur seperti seharusnya. Dua mode yang
       * sama kodenya terlihat berbeda kelakuannya, dan yang membedakan cuma
       * apakah tangannya sempat menekan sesuatu.
       */
      const fokus = document.activeElement;
      const dipegang =
        grupGridRef.current?.contains(fokus) || grupTransportRef.current?.contains(fokus);
      if (dipegang && fokus instanceof HTMLElement && fokus.matches(':focus-visible')) {
        tidurRef.current = window.setTimeout(tidur, KENDALI_TIDUR_MS);
        return;
      }
      setKendaliTampak(false);
    }, KENDALI_TIDUR_MS);
  }, []);

  useEffect(() => () => window.clearTimeout(tidurRef.current), []);

  /**
   * Fokus sedang berada di dalam salah satu kelompok tombol kendali slideshow.
   *
   * Dipakai penangan panah di bawah: di dalam kelompok, Kiri/Kanan milik
   * navigasi antar tombol, bukan ganti halaman. Tanpa pembedaan ini remote TV
   * tidak punya cara apa pun memilih tata letak — fokusnya sampai di pemilih
   * grid, lalu setiap Kiri/Kanan malah memindahkan halaman di belakangnya.
   */
  const diKelompokKendali = useCallback(() => {
    const f = document.activeElement;
    if (!f) return false;
    return !!(grupGridRef.current?.contains(f) || grupTransportRef.current?.contains(f));
  }, []);

  const searchRef = useRef<HTMLInputElement>(null);
  // Tab yang tersembunyi tidak perlu menerima frame: hemat bandwidth klien dan
  // proses Apache, dan tidak ada yang melihatnya (§D3.2).
  const [tabTerlihat, setTabTerlihat] = useState(() => !document.hidden);

  const load = useCallback(async () => {
    try {
      const r = await api<{ cameras: Camera[] }>('/cameras');
      setCameras(r.cameras);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = window.setInterval(() => void load(), STATUS_POLL_MS);
    return () => window.clearInterval(t);
  }, [load]);

  /**
   * Isi monitor ditarik sekali, tidak ikut polling status 30 detik: susunannya
   * hanya berubah kalau pemiliknya sendiri menyuntingnya, dan itu terjadi di
   * halaman lain.
   *
   * Monitor yang tidak ketemu — dihapus di tab sebelah, atau id yang diketik
   * asal — jatuh kembali ke seluruh kamera, bukan ke layar kosong: yang terjadi
   * bukan kesalahan yang perlu diteriakkan, dan dashboard penuh adalah keadaan
   * yang benar untuk alamat tanpa monitor.
   */
  /**
   * Dashboard terakhir yang dibuka diingat **per akun di browser ini** —
   * tutup browser atau logout, buka lagi, langsung kembali ke sana (Zaki,
   * 6 Okt 2026). Kuncinya memuat id user, jadi PC pos yang dipakai bergantian
   * tidak saling menimpa.
   */
  const navigate = useNavigate();
  const jenisNavigasi = useNavigationType();
  const kunciTerakhir = user ? `lensa.dashboard.terakhir.${user.id}` : null;

  useEffect(() => {
    if (!monitorId) {
      setMonitor(null);
      // Klik "Dashboard" di sidebar (PUSH) = memang mau dashboard utama, dan
      // itu yang diingat. Selain itu (buka aplikasi, sesudah login, redirect
      // dari `/`) kembali ke monitor terakhir kalau ada.
      if (jenisNavigasi === 'PUSH') ingatDashboard(kunciTerakhir, null);
      else {
        const id = bacaDashboard(kunciTerakhir);
        if (id) navigate(`/dashboard?monitor=${encodeURIComponent(id)}`, { replace: true });
      }
      return;
    }
    let hidup = true;
    void api<{ monitors: { id: string; name: string; cameraIds: string[] }[] }>('/monitors')
      .then((r) => {
        if (!hidup) return;
        const m = r.monitors.find((x) => x.id === monitorId);
        setMonitor(m ? { name: m.name, cameraIds: m.cameraIds } : null);
        // Monitor yang sudah dihapus tidak boleh terus jadi tujuan pulang.
        ingatDashboard(kunciTerakhir, m ? monitorId : null);
      })
      .catch(() => undefined);
    return () => {
      hidup = false;
    };
    // `jenisNavigasi` sengaja bukan dependensi: yang menentukan hanya cara
    // halaman ini didatangi saat alamatnya berubah.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monitorId, kunciTerakhir]);

  /**
   * Penanda monitor muncul dan hilang di DALAM bilah kendali, sementara lebar
   * grid tidak bergeser sedikit pun — dan pengamat di atas sengaja tuli
   * terhadap perubahan tinggi murni (alasannya panjang, ada di callback
   * `ResizeObserver`). Tanpa pembacaan ulang di sini, masuk ke sebuah monitor
   * pada lebar yang membuat bilahnya membungkus meninggalkan `kotak.h` yang
   * basi: grid sudah kehilangan setinggi satu baris bilah, tapi pilihan tata
   * letaknya masih jawaban untuk grid yang lama.
   *
   * Ini tidak menghidupkan lagi lingkaran yang tadi diputus: pembacaannya
   * sekali jalan dan dipicu oleh `monitor` yang berubah, bukan oleh tinggi yang
   * bergeser. Pilihan tata letak yang ikut berubah tidak bisa memanggil
   * pembacaan berikutnya.
   *
   * Ditunda satu frame karena tinggi bilahnya baru benar sesudah penandanya
   * ikut ditata.
   */
  useEffect(() => {
    const bingkai = requestAnimationFrame(bacaUlangKotak);
    return () => cancelAnimationFrame(bingkai);
  }, [monitor, bacaUlangKotak]);

  useEffect(() => {
    // Memutar ponsel memicu `resize` juga, jadi tidak perlu `orientationchange`
    // sendiri — yang berubah cuma pasangan angkanya.
    const onResize = () => {
      setViewport({ w: window.innerWidth, h: window.innerHeight });
      // Jendela yang hanya berubah TINGGI tidak menggeser lebar grid, jadi
      // pengamat di atas sengaja mendiamkannya. Di sini perubahan itu memang
      // datang dari luar, jadi kotaknya dibaca ulang apa adanya.
      bacaUlangKotak();
    };
    const onVisible = () => setTabTerlihat(!document.hidden);
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [bacaUlangKotak]);

  // `/auth/me` sering selesai sesudah render pertama, jadi nilai awal di atas
  // belum tentu sempat melihat pilihan tersimpan. Efek ini yang menerapkannya
  // begitu user-nya datang — sekali saja, dan hanya kalau user di halaman ini
  // belum menyentuh kendali tata letak.
  useEffect(() => {
    if (tersimpan === null || layoutDipilihUser.current) return;
    layoutDipilihUser.current = true;
    setLayout(tersimpan);
  }, [tersimpan]);

  /**
   * Pilihan dihitung dari kotak grid; nilai awalnya tetap membaca ukuran layar.
   *
   * Dua pertanyaan yang berbeda: "tata letak mana yang muat di kotak ini" itu
   * geometri, sementara "berapa banyak yang sebaiknya ditampilkan lebih dulu"
   * itu keputusan operasional — TV pos satpam sengaja mulai dari 4 tile besar
   * walaupun layarnya sanggup 25 (§G8.1).
   */
  const options = useMemo(() => pilihanUntuk(kotak.w, kotak.h), [kotak.w, kotak.h]);

  /**
   * Yang digambar sebagai tombol: daftar pendek di atas **plus** tata letak yang
   * sedang dipakai, kalau ia kebetulan tidak ada di dalamnya (25 Agustus 2026).
   *
   * Sejak tata letak yang sah dipertahankan (efek di bawah), pemilih ini bisa
   * saja menerima pilihan yang bukan salah satu dari enam — dan pemilih tanpa
   * satu pun tombol menyala tidak memberi tahu apa yang sedang tampil, apalagi
   * jalan kembali ke sana sesudah menekan yang lain.
   */
  const pilihanTampil = useMemo(() => pilihanDengan(layout, options), [layout, options]);

  /**
   * Layar berubah (TV → tablet, atau ponsel diputar) tidak boleh meninggalkan
   * tata letak yang tidak layak lagi untuk kotak barunya. Selama pilihan user
   * masih **sah**, dia dihormati.
   *
   * "Sah" di sini berarti syarat geometrinya — tilenya masih terbaca dan
   * isiannya masih lebih banyak daripada ruang kosongnya (`sahUntuk`) — bukan
   * "masuk enam tombol yang ditawarkan" (25 Agustus 2026). Dulu yang dipakai
   * `options.includes(cur)`, dan itu yang membuang tata letak pilihan orang
   * setiap kali masuk layar penuh: kotak dashboard 1696×1013 menawarkan 4×5,
   * kotak layar penuh 1920×1080 tidak — bukan karena 4×5 tidak muat di sana
   * (tilenya 384px, isian 80%) melainkan karena di kotak 16:9 ada enam tata
   * letak bujur sangkar yang mengisi 100%. Orang yang memilih 4×5 lalu menekan
   * Fullscreen mendarat di 2×2, jauh dari apa pun yang dia minta.
   *
   * Kalau memang tidak sah lagi, yang dicari lebih dulu adalah pilihan dengan
   * **jumlah tile yang sama**, baru default ukuran itu. Yang menyelamatkan
   * rotasi ponsel: 1×4 dan 2×2 sama-sama empat tile, 1×2 dan 2×1 sama-sama dua,
   * jadi memutar perangkat bolak-balik mengembalikan pilihan yang sama — bukan
   * diam-diam menurunkannya ke default tiap kali.
   */
  useEffect(() => {
    setLayout((cur) => {
      if (sahUntuk(cur, kotak.w, kotak.h)) return cur;
      const setara = options.find((l) => gridSel(l) === gridSel(cur));
      return setara ?? bawaanUntuk(viewport.w, viewport.h, options);
    });
  }, [viewport.w, viewport.h, kotak.w, kotak.h, options]);

  /**
   * Turunkan default kalau kameranya lebih sedikit daripada selnya (§G8, catatan
   * "menyusut ke jumlah kamera").
   *
   * Rasio tile terkunci 16:9, jadi tata letak 3 kolom berisi 4 kamera tidak bisa
   * mengisi tinggi layar dengan cara apa pun — sisanya jadi ruang mati, dan di
   * ruang monitor ruang mati itu artinya gambar yang lebih kecil dari yang bisa
   * didapat. Empat kamera pada 2×2 memakai ±83% viewport; pada 3×3 hanya ±36%.
   *
   * Hanya berlaku sebagai NILAI AWAL. Pilihan eksplisit user tidak pernah
   * ditimpa, dan angkanya tidak pernah naik melebihi default §G8 untuk lebar itu.
   */
  useEffect(() => {
    if (layoutDipilihUser.current || !cameras || cameras.length === 0) return;
    const def = bawaanUntuk(viewport.w, viewport.h, options);
    // Pilihan terkecil yang masih memuat semua kamera. Tidak pernah lebih besar
    // daripada default ukuran layar ini — batas atas §G8 tetap batas atas.
    const muat = [...options]
      .sort((a, b) => gridSel(a) - gridSel(b))
      .find((l) => gridSel(l) >= cameras.length);
    setLayout(muat && gridSel(muat) < gridSel(def) ? muat : def);
    // Sengaja hanya bergantung pada jumlah kamera: perubahan lebar sudah
    // ditangani efek di atas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameras?.length]);

  const terlihat = useMemo(() => {
    let list = cameras ?? [];
    if (monitor) {
      // Urutan mengikuti susunan monitornya, bukan urutan nama: orang yang
      // menaruh gerbang depan di kotak pertama menaruhnya di sana dengan alasan.
      const urut = new Map(monitor.cameraIds.map((id, i) => [id, i]));
      list = list
        .filter((c) => urut.has(c.id))
        .sort((a, b) => urut.get(a.id)! - urut.get(b.id)!);
    }
    // Nama, host, dan path — pencocokan yang sama persis dengan `/cameras`,
    // supaya kotak yang berbunyi sama mencari hal yang sama juga.
    const q = query.trim();
    return q ? list.filter((c) => cocokKamera(c, q)) : list;
  }, [cameras, query, monitor]);

  const { kolom } = gridDim(layout);
  const perPage = gridSel(layout);
  const pageCount = Math.max(1, Math.ceil(terlihat.length / perPage));
  const pageAman = Math.min(page, pageCount - 1);
  const mulai = pageAman * perPage;
  const halaman = terlihat.slice(mulai, mulai + perPage);

  useEffect(() => setPage(0), [query, layout]);

  /**
   * Ponsel tegak: tile mengisi lebar penuh dan gridnya yang menggulung (§G8, 31
   * Agustus 2026, diminta lapangan).
   *
   * Di satu kolom, sel gridnya hampir selalu lebih pendek daripada 16:9 selebar
   * layar — tiga atau empat baris dibagi dari tinggi yang sudah dipotong header
   * dan bilah kendali. Tile yang wajib muat di dalam selnya lalu menyempit
   * sambil menjaga rasio, dan yang tersisa di kiri-kanannya batang hitam selebar
   * puluhan piksel: *"kotak frame bordernya lebih gede dr pada gambar videonya,
   * kiri kanannya keisi hitam."* Yang ditukar di sini adalah syarat yang
   * membuatnya menyempit — "seluruh grid harus terlihat tanpa menggulung" —
   * dengan gulungan, yang memang ditawarkan lapangan: *"kalo misalnya jd panjang
   * kebawah, ya discroll gpp kok."*
   *
   * **Hanya di panel dashboard, tidak di layar penuh/slideshow.** Layar penuh
   * ada untuk memenuhi layar; layar penuh yang menggulung berarti ada kamera
   * yang tidak terlihat sampai ada yang menggulungnya, dan di TV pos satpam
   * tidak ada yang menggulung apa pun.
   *
   * Syaratnya **satu kolom + layar tegak**, bukan lebar layar di bawah angka
   * tertentu. Satu kolom adalah bentuk yang selalu diberikan penyaring `§G8`
   * untuk ponsel tegak (dua kolom pada 390px memberi tile 195px dan tersaring
   * pagar 220px), dan ia sekaligus satu-satunya bentuk yang benar-benar bisa
   * memakai lebar penuh tanpa menyempit — di dua kolom, melebarkan tile berarti
   * mendorong tetangganya keluar layar. Tablet tegak yang mendarat di 1×3 ikut
   * kebagian, dan memang seharusnya: keluhannya sama persis di sana.
   *
   * Dibaca dari ukuran **layar**, bukan dari kotak grid yang diukur: kotaknya
   * justru yang ikut berubah begitu ini menyala, dan syarat yang membaca
   * hasilnya sendiri adalah syarat yang berkedip.
   */
  const memanjang = !slideshow && kolom === 1 && viewport.h >= viewport.w;

  /**
   * Tinggi baris di mode itu **dihitung**, tidak diserahkan ke `auto`
   * (31 Agustus 2026, perbaikan hari yang sama).
   *
   * Bentuk pertamanya `repeat(N, auto)`, dan hasilnya seluruh tile menumpuk jadi
   * satu di puncak grid — dilaporkan lapangan sebagai *"card kamera2 framenya
   * malah numpuk kaya 4 kartu ditumpuk gitu."* Sebabnya lingkaran yang tidak
   * kelihatan: baris `auto` menagih tinggi dari isinya, tinggi tile datang dari
   * `aspect-ratio` di atas lebarnya, dan lebar itu sendiri persentase yang baru
   * pasti sesudah barisnya selesai diukur. Saat menghitung tinggi baris, browser
   * memakai lebar **max-content** tile — dan seluruh isi tile adalah lapisan
   * `absolute` (gambar, poster, overlay nama), jadi max-content-nya nol. Nol
   * lebar × 16:9 = nol tinggi: ketiga atau keempat barisnya setinggi nol, dan
   * tile yang tetap digambar setinggi 219px tumpah keluar barisnya masing-masing
   * ke tempat yang sama.
   *
   * Lebar kolomnya sudah kita ketahui — `kotak.w` diukur `ResizeObserver`, dan
   * di satu kolom ia **adalah** lebar tile — jadi tingginya tinggal dikalikan
   * 9/16. Angka pasti memutus lingkarannya sekaligus: baris tidak lagi bertanya
   * kepada isinya.
   */
  const tinggiBaris = memanjang ? Math.round((kotak.w * 9) / 16) : 0;

  /**
   * Halaman baru selalu dimulai dari atas — hanya berlaku saat gridnya memang
   * menggulung.
   *
   * Tanpa ini, menekan `›` sambil berada di bawah meninggalkan orangnya di
   * tengah halaman berikutnya: kamera pertama halaman itu ada di atas, tidak
   * terlihat, dan tidak ada apa pun di layar yang mengabarkan bahwa halamannya
   * sudah berganti.
   */
  useEffect(() => {
    if (!memanjang) return;
    elGridRef.current?.scrollTo({ top: 0 });
  }, [memanjang, pageAman]);

  /**
   * Tata letak 1×1: kamera yang tampil **adalah** kamera yang sedang ditunjuk,
   * tanpa perlu ada yang menunjuknya (§D3.6). Ini jalur pos satpam — satu layar,
   * satu kamera, dan tombol layar penuh yang ditekan tanpa tetikus.
   *
   * Sengaja hanya 1×1. Pada 2×2 ke atas tidak ada satu kamera pun yang bisa
   * disebut "yang dipilih", dan memanaskan semuanya berarti menyalakan puluhan
   * transcode HD untuk tebakan yang hampir selalu meleset.
   */
  const kameraTunggal = perPage === 1 ? (halaman[0]?.id ?? null) : null;

  useEffect(() => {
    if (kameraTunggal === null) return;
    tunjukKamera(kameraTunggal);
    return () => tunjukKamera(null);
    // Sengaja bergantung pada **id**-nya, bukan pada `halaman`. `halaman` itu
    // array baru di setiap render, jadi efek yang bergantung padanya akan
    // membersihkan dan memasang dirinya berulang kali — dan setiap pembersihan
    // menyetel ulang jeda 350 ms milik `tunjukKamera`, sehingga pra-panasnya
    // tidak pernah benar-benar terkirim.
  }, [kameraTunggal]);

  const fullscreenCamera = fullscreenId
    ? (cameras?.find((c) => c.id === fullscreenId) ?? null)
    : null;

  /**
   * Kamera sebelah saat layar penuh (23 Agu 2026).
   *
   * Menjelajah seluruh `terlihat` — daftar yang sudah tersaring pencarian dan
   * hak akses — bukan hanya kamera di halaman yang sedang tampil. "Kamera
   * berikutnya" yang berhenti di ujung halaman akan terbaca sebagai kamera yang
   * hilang, dan orang yang menyusuri kamera memang sedang menyusuri semuanya.
   *
   * Halaman di belakangnya ikut digeser supaya saat viewer ditutup, tile yang
   * barusan dilihat memang ada di layar — kalau tidak, orangnya kembali ke
   * halaman lama tanpa tahu di mana tadi berhenti.
   */
  const idxFullscreen = fullscreenId ? terlihat.findIndex((c) => c.id === fullscreenId) : -1;

  const geserFullscreen = useCallback(
    (arah: 1 | -1) => {
      if (terlihat.length === 0 || idxFullscreen < 0) return;
      const next = (idxFullscreen + arah + terlihat.length) % terlihat.length;
      const cam = terlihat[next];
      if (!cam) return;
      setFullscreenId(cam.id);
      setPage(Math.floor(next / perPage));
      setSelected(next % perPage);
    },
    [idxFullscreen, terlihat, perPage],
  );

  /**
   * Interaksi apa pun **keluar** dari slideshow (§G7 no.6).
   *
   * Dibedakan dari `jalan` di bawah sejak §G8.2: yang ini membongkar mode layar
   * penuhnya, sementara jeda hanya menghentikan lajunya. Keduanya dulu satu
   * tombol, dan akibatnya menghentikan perpindahan berarti kehilangan layar
   * penuhnya sekalian — padahal yang diinginkan operator saat menekan jeda
   * justru sebaliknya: berhenti di kamera ini, tetap sebesar ini.
   */
  const jedaSlideshow = useCallback(() => {
    setSlideshow(false);
    setManual(false);
    setCountdown(interval);
  }, [interval]);

  const halamanBerikut = useCallback(() => {
    setPage((p) => (p + 1) % pageCount);
  }, [pageCount]);

  /** Maju/mundur satu halaman **tanpa** keluar dari slideshow (§G8.2). */
  const pindahHalaman = useCallback(
    (arah: 1 | -1) => {
      setPage((p) => (p + arah + pageCount) % pageCount);
      // Hitung mundur direset: halaman yang baru saja dipilih tangan tidak boleh
      // berpindah lagi setengah detik kemudian karena sisa hitungan sebelumnya.
      setCountdown(interval);
    },
    [pageCount, interval],
  );

  /**
   * Sapuan jari kiri/kanan = halaman berikutnya/sebelumnya (§G8.2, 31 Agustus
   * 2026, diminta lapangan untuk ponsel dan layar sentuh).
   *
   * Pil `‹`/`›` tetap ada — ia satu-satunya kendali yang bisa dipakai remote TV
   * dan tetap yang menerangkan bahwa halamannya memang bisa dipindah. Yang
   * dipotong sapuan adalah langkah menuju pil itu: kendalinya tidur sesudah 4
   * detik, jadi berpindah halaman dengan jari sebelumnya berarti ketuk dulu
   * untuk membangunkan, cari pilnya, baru tekan.
   *
   * Mati selama viewer satu kamera terbuka: gestur di sana miliknya sendiri
   * (cubit dan geser untuk zoom, §G8.2), dan kejadiannya menggelembung sampai
   * ke sini karena viewer-nya memang anak dari overlay ini. Mati juga kalau
   * halamannya cuma satu — sapuan yang tidak mengubah apa pun terbaca sebagai
   * gestur yang tidak sampai.
   */
  const geserHalaman = useGeserJari(pindahHalaman, !fullscreenCamera && pageCount > 1);

  useEffect(() => {
    if (!slideshow || !jalan || fullscreenCamera) return;
    setCountdown(interval);
    const t = window.setInterval(() => {
      setCountdown((c) => {
        if (c > 1) return c - 1;
        halamanBerikut();
        return interval;
      });
    }, 1_000);
    return () => window.clearInterval(t);
  }, [slideshow, jalan, interval, halamanBerikut, fullscreenCamera, pageAman]);

  /**
   * Jawaban tunggal untuk "panah ini milik siapa", dipakai pembungkus TV
   * **dan** penangan `keydown` di bawah. Satu sumber, bukan dua salinan.
   *
   * - viewer terbuka  — bukan urusan halaman ini;
   * - slideshow jalan — Kiri/Kanan berarti ganti halaman, **kecuali** saat
   *                     fokus sedang menelusuri tile atau tombol kendali. Tanpa
   *                     pengecualian itu, grid 3 kolom punya tile tengah yang
   *                     tidak bisa dicapai sama sekali: satu-satunya jalan ke
   *                     sana mendatar, dan mendatar selalu memindahkan halaman;
   * - selebihnya      — kursor tile, tapi hanya selama fokus ada di dalam grid
   *                     dan tujuannya masih ada. Di tepi grid panahnya dilepas,
   *                     dan itu satu-satunya jalan keluar menuju bilah kendali.
   */
  const klaimPanah = useCallback(
    (arah: ArahPanah) => {
      if (fullscreenId) return false;
      const diGrid = !!elGridRef.current?.contains(document.activeElement);

      if (slideshow) {
        if (arah !== 'left' && arah !== 'right') return false;
        return !diGrid && !diKelompokKendali();
      }

      if (!diGrid) return false;
      const delta =
        arah === 'right' ? 1 : arah === 'left' ? -1 : arah === 'down' ? kolom : -kolom;
      return Math.max(0, Math.min(halaman.length - 1, selected + delta)) !== selected;
    },
    [fullscreenId, slideshow, diKelompokKendali, kolom, halaman.length, selected],
  );

  usePanahTv(klaimPanah);

  /**
   * Menaruh fokus DOM pada tile yang sedang ditunjuk kursor papan ketik.
   *
   * Sampai 27 Agustus 2026 `selected` cuma state React: ia menggambar cincin,
   * tapi tidak memegang fokus apa pun. Di tetikus dan papan ketik lengkap itu
   * cukup — tile dibuka dengan klik atau dengan `F`. Di remote TV tidak: yang
   * ada cuma panah, OK, dan Back. Kursor yang tidak memegang fokus berarti OK
   * tidak punya sasaran, dan seluruh remote terbaca sebagai mati total.
   *
   * Sasarannya tombol layar penuh milik tile itu, karena itu yang diharapkan
   * terjadi saat menekan OK di sebuah kamera.
   */
  const fokuskanTile = useCallback((i: number) => {
    // Ditunda satu tick: tile tujuannya baru ada sesudah render berikutnya.
    window.setTimeout(() => {
      elGridRef.current?.querySelectorAll<HTMLElement>('[data-tile-fs]')[i]?.focus();
    }, 0);
  }, []);

  // Papan ketik §G7 no.8.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const diInput =
        e.target instanceof HTMLElement &&
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName);

      if (e.key === '/' && !diInput) {
        e.preventDefault();
        searchRef.current?.focus();
        return;
      }
      if (diInput) return;
      if (fullscreenId) return; // overlay punya penanganannya sendiri
      /**
       * Membangunkan kendali dikerjakan **di dalam penangan ini**, bukan oleh
       * listener `keydown` kedua (25 Agustus 2026).
       *
       * Dua listener `keydown` di `window` tidak bisa hidup berdampingan di
       * sini, dan sebabnya halus: efek yang memasang penangan ini bergantung
       * pada `halaman` — array baru tiap render — jadi ia **melepas dan memasang
       * ulang dirinya setiap kali komponennya render. Kalau listener yang lain
       * berjalan lebih dulu dan mengubah state (membangunkan kendali yang
       * sedang tidur adalah perubahan state), React menyiram render itu di
       * pemeriksaan microtask **di antara dua listener** — dan DOM tidak pernah
       * memanggil listener yang dilepas di tengah penyiaran, juga tidak
       * memanggil yang baru dipasang. Tombolnya hilang tanpa jejak.
       *
       * Yang terlihat di lapangan: tekanan **pertama** sesudah kendalinya tidur
       * cuma memunculkan kendali dan tidak melakukan apa-apa; tekanan kedua
       * baru bekerja. Persis gejala "Esc harus dua kali" yang sudah diperbaiki
       * di tempat lain, muncul lagi lewat pintu yang berbeda. Terukur di
       * `lensa-dev.sandya.net`: Esc → tidak terjadi apa-apa → Esc → baru keluar;
       * `PageDown` pertama tidak memindahkan halaman, yang kedua memindahkan.
       *
       * Satu penangan menyelesaikannya: listener yang **sedang** berjalan tidak
       * bisa dibatalkan oleh render yang dipicunya sendiri.
       */
      if (slideshow) {
        // Sesudah penjaga `fullscreenId` dengan sengaja: selama viewer satu
        // kamera terbuka, papan ketik miliknya — dan mengubah state di sini
        // akan menelan tombolnya persis seperti yang dijelaskan di atas.
        bangunkanKendali();
        /**
         * Sekalian memulihkan layar penuh browser yang lepas saat Esc menutup
         * viewer satu kamera — `requestFullscreen` menolak permintaan tanpa
         * gestur pemakai, jadi ia menumpang tombol berikutnya. Esc sendiri
         * dikecualikan: di mode ini Esc berarti keluar.
         */
        if (perluPenuhLagi.current && e.key !== 'Escape') {
          perluPenuhLagi.current = false;
          if (!document.fullscreenElement) mintaLayarPenuh();
        }
      }


      /**
       * Selama slideshow berjalan, papan ketik mengendalikan **slideshow-nya**,
       * bukan kursor tile (§G8.2).
       *
       * Sebelumnya semua tombol ini memanggil `jedaSlideshow()`, yang berarti
       * menekan panah untuk melihat halaman sebelah justru membongkar layar
       * penuhnya. Di TV pos satpam — yang cuma punya papan ketik — itu membuat
       * slideshow praktis tidak bisa dikemudikan sama sekali.
       */
      if (slideshow) {
        switch (e.key) {
          case ' ':
            e.preventDefault();
            // Mode manual memang tidak punya jeda/lanjut (§G8.2): tombol yang
            // tidak ada di layar tidak boleh punya jalan pintas yang bekerja.
            if (!manual) setJalan((v) => !v);
            return;
          /**
           * `G` memanggil pemilih tata letak dan langsung menaruh fokus di
           * dalamnya (§G8.2).
           *
           * Tanpa ini pemilih itu hanya bisa dipanggil dengan menggerakkan
           * penunjuk — dan TV pos satpam tidak punya penunjuk. Fokusnya
           * dipindahkan sekalian karena memunculkan sesuatu yang tetap harus
           * dicari dengan Tab bukan jalan pintas, itu cuma langkah tambahan.
           */
          case 'g':
          case 'G': {
            e.preventDefault();
            bangunkanKendali();
            window.setTimeout(
              () => grupGridRef.current?.querySelector('button')?.focus(),
              0,
            );
            return;
          }
          case 'ArrowRight':
          case 'ArrowLeft': {
            /*
             * Dilepas — tidak dikonsumsi — selama fokus ada di dalam kelompok
             * tombol kendali. Pembungkus TV yang meneruskannya jadi perpindahan
             * fokus antar tombol, dan itu satu-satunya cara remote memilih tata
             * letak. `PageUp`/`PageDown` di bawah tetap tanpa syarat: keduanya
             * memang berarti "ganti halaman" di mana pun fokusnya berada.
             */
            if (!klaimPanah(e.key === 'ArrowRight' ? 'right' : 'left')) return;
            e.preventDefault();
            pindahHalaman(e.key === 'ArrowRight' ? 1 : -1);
            return;
          }
          case 'PageDown':
            e.preventDefault();
            pindahHalaman(1);
            return;
          case 'PageUp':
            e.preventDefault();
            pindahHalaman(-1);
            return;
          case 'Escape':
            e.preventDefault();
            jedaSlideshow();
            return;
          default:
            return;
        }
      }

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowLeft':
        case 'ArrowDown':
        case 'ArrowUp': {
          /*
           * Satu pertanyaan yang sama dengan yang dijawab ke pembungkus TV:
           * fokus harus di dalam grid, dan tujuannya harus ada. Di tepi grid
           * panahnya dilepas — itu satu-satunya jalan keluar menuju bilah
           * kendali di remote yang cuma punya empat arah.
           */
          const arah: ArahPanah =
            e.key === 'ArrowRight' ? 'right'
              : e.key === 'ArrowLeft' ? 'left'
                : e.key === 'ArrowDown' ? 'down' : 'up';
          if (!klaimPanah(arah)) return;

          const delta =
            e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowDown' ? kolom : -kolom;
          const next = Math.max(0, Math.min(halaman.length - 1, selected + delta));

          e.preventDefault();
          jedaSlideshow();
          setSelected(next);
          fokuskanTile(next);
          // Kursor papan ketik adalah "menunjuk" versi TV pos satpam, yang
          // tidak punya penunjuk sama sekali (§D3.6). Dipanggil di sini, bukan
          // di dalam efek yang mengamati `selected`: efek seperti itu akan
          // ikut menyala setiap pergantian halaman dan memanaskan tile
          // pertama dari setiap halaman yang cuma dilewati.
          tunjukKamera(halaman[next]?.id ?? null);
          break;
        }
        case 'f':
        case 'F': {
          const cam = halaman[selected];
          if (cam) {
            jedaSlideshow();
            setFullscreenId(cam.id);
          }
          break;
        }
        case ' ':
          e.preventDefault();
          setManual(false);
          setJalan(true);
          setSlideshow(true);
          break;
        // Esc saat slideshow ditangani di blok di atas (§G8.2).
        case 'PageDown':
          jedaSlideshow();
          setPage((p) => Math.min(pageCount - 1, p + 1));
          break;
        case 'PageUp':
          jedaSlideshow();
          setPage((p) => Math.max(0, p - 1));
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    halaman,
    selected,
    kolom,
    pageCount,
    fullscreenId,
    jedaSlideshow,
    slideshow,
    manual,
    pindahHalaman,
    bangunkanKendali,
    fokuskanTile,
    klaimPanah,
  ]);

  /**
   * Cermin `fullscreenId` untuk penangan `fullscreenchange` di bawah.
   *
   * Efek itu **tidak boleh** bergantung pada `fullscreenId`: menjalankannya
   * ulang berarti `exitFullscreen` lalu `requestFullscreen` lagi, dan
   * permintaan kedua tidak berasal dari gestur pemakai — browser menolaknya dan
   * layar penuhnya hilang tepat saat kamera dibuka.
   */
  const fullscreenIdRef = useRef<string | null>(null);
  fullscreenIdRef.current = fullscreenId;

  /**
   * Layar penuh browser yang hilang karena Esc menutup viewer, bukan karena
   * orangnya ingin keluar — dipulihkan pada tindakan pertama yang punya gestur
   * pemakai (lihat efek tanda kehidupan dan efek layar penuh di bawah).
   */
  const perluPenuhLagi = useRef(false);

  /**
   * Tanda kehidupan apa pun membangunkan kendali slideshow (§G8.2).
   *
   * Tiga pemicu, dan ketiganya perlu sejak transport ikut menyembunyikan diri:
   * `pointermove` untuk tetikus, `pointerdown` untuk layar sentuh — sentuhan
   * TIDAK pernah menghasilkan `pointermove`, jadi tanpa ini satu ketukan di
   * tablet tidak memunculkan apa pun dan kendalinya jadi benar-benar tak
   * terjangkau — dan `keydown` untuk TV pos satpam yang cuma punya papan ketik.
   *
   * `keydown` sengaja menangkap tombol apa pun, bukan hanya yang punya arti:
   * Space menjeda dan ←/→ pindah halaman, dan kalau kendalinya tetap gelap
   * saat ditekan orang tidak punya cara tahu bahwa tekanannya sampai.
   *
   * Masuk ke slideshow juga dihitung tanda kehidupan — menekan tombol
   * "Slideshow" lalu disambut layar tanpa satu pun kendali terbaca seperti
   * aplikasi yang menggantung.
   *
   * Hanya selama slideshow: di panel dashboard biasa kendalinya memang menetap
   * di bilah atas dan tidak ada yang perlu dibangunkan. Keluar dari slideshow
   * mengembalikannya ke keadaan tersembunyi supaya masuk berikutnya tidak
   * dimulai dengan kendali yang sudah menyala tanpa diminta.
   */
  useEffect(() => {
    if (!slideshow) {
      window.clearTimeout(tidurRef.current);
      setKendaliTampak(false);
      perluPenuhLagi.current = false;
      return;
    }
    bangunkanKendali();
    const onGerak = () => bangunkanKendali();
    /**
     * Sentuhan/klik: bangunkan kendali, dan sekalian pulihkan layar penuh
     * browser yang lepas saat Esc menutup viewer satu kamera (25 Agustus 2026).
     * `requestFullscreen` menolak permintaan tanpa gestur pemakai, jadi
     * pemulihannya memang harus menumpang tindakan berikutnya.
     */
    const onTindakan = () => {
      bangunkanKendali();
      if (!perluPenuhLagi.current) return;
      perluPenuhLagi.current = false;
      if (!document.fullscreenElement) mintaLayarPenuh();
    };
    /**
     * **`keydown` sengaja tidak ada di sini.** Papan ketik dibangunkan dari
     * dalam penangan papan ketik halaman ini — listener `keydown` kedua akan
     * menelan tekanan pertama; alasan lengkapnya ada di sana.
     */
    window.addEventListener('pointermove', onGerak);
    window.addEventListener('pointerdown', onTindakan);
    return () => {
      window.removeEventListener('pointermove', onGerak);
      window.removeEventListener('pointerdown', onTindakan);
    };
  }, [slideshow, bangunkanKendali]);

  /**
   * Layar penuh browser mengikuti mode layar penuh grid (§G8.1).
   *
   * Dua arah, dan arah keduanya yang penting: kalau orang keluar dari layar
   * penuh lewat Esc atau F11 milik browser, modenya ikut berhenti. Tanpa itu
   * halaman kembali ke tampilan biasa sementara slideshow masih berpindah
   * halaman sendiri di belakangnya — keadaan yang tidak bisa dijelaskan ke
   * siapa pun.
   *
   * **Halaman ini pemilik tunggal layar penuh browser selama mode ini hidup**
   * (25 Agustus 2026). Viewer satu kamera yang dibuka dari dalam sini menumpang
   * (`bersarang`), tidak memintanya sendiri — dan itu yang memperbaiki tiga
   * gejala sekaligus yang dilaporkan lapangan:
   *
   * 1. Esc harus ditekan **dua kali**. Saat layar penuh browser aktif, Esc
   *    dimakan browser dan `keydown`-nya tidak pernah sampai ke halaman, jadi
   *    tekanan pertama cuma mengecilkan gambar.
   * 2. Tekanan kedua mendarat di **dashboard**, bukan kembali ke grid layar
   *    penuh: keluarnya layar penuh dibaca penangan ini sebagai "bongkar
   *    modenya", padahal yang ditutup orangnya cuma kameranya.
   * 3. Menutup viewer lewat tombol "Close (Esc)" pun membongkar modenya, karena
   *    viewer yang pergi memanggil `exitFullscreen` miliknya sendiri.
   *
   * Jadi keluarnya layar penuh sekarang dibaca berlapis: **kalau ada kamera
   * yang sedang dibuka, yang ditutup kameranya** — satu Esc, kembali ke grid,
   * persis seperti Esc di mana pun di aplikasi ini. Baru Esc berikutnya
   * membongkar modenya.
   *
   * Layar penuh browsernya sendiri memang ikut lepas pada Esc pertama (tidak
   * ada cara mencegahnya — Esc milik browser), dan grid layar penuhnya bertahan
   * sebagai overlay setinggi viewport sampai `perluPenuhLagi` sempat
   * memulihkannya.
   */
  useEffect(() => {
    if (!slideshow) return;
    const keluar = mintaLayarPenuh();
    const onChange = () => {
      if (document.fullscreenElement) return;
      if (fullscreenIdRef.current) {
        setFullscreenId(null);
        perluPenuhLagi.current = true;
        return;
      }
      setSlideshow(false);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      keluar();
    };
  }, [slideshow]);

  // Hanya kalau belum pernah punya daftar. Satu poll yang gagal (API sedang
  // restart) tidak boleh membongkar grid yang sedang ditonton: tile-nya punya
  // penyambung ulang sendiri, dan poll berikutnya memulihkan daftarnya.
  if (error && !cameras) {
    return (
      <p className="p-8 text-sm text-amber-600" role="alert">
        {t('Could not load cameras: {pesan}', { pesan: error })}
      </p>
    );
  }

  if (!cameras) {
    return (
      <p className="p-8 text-sm text-muted" role="status">
        {t('Loading cameras…')}
      </p>
    );
  }

  /**
   * `aksi` mematikan ikon pojok tile saat slideshow berjalan.
   *
   * Bilah "Stop (Esc)" duduk di pojok kanan atas layar, dan pada tata letak
   * 2×2 pojok itu persis tempat ikon layar penuh tile pertama berada — yang
   * satu menutupi yang lain. Slideshow juga tidak punya operator yang menekan
   * ikon: yang ditawarkannya cuma satu, berhenti. Jadi ikonnya ditiadakan,
   * bukan digeser.
   */
  const gridEl = (aksi: boolean) => (
    <div
      ref={gridRef}
      /* Ponsel tegak menggulung; selebihnya seluruh grid wajib muat sekaligus.
         `content-start` supaya baris-barisnya menumpuk dari atas alih-alih
         diregangkan memenuhi kotak yang sekarang lebih pendek daripada isinya. */
      className={`grid min-h-0 flex-1 gap-2 p-2 ${
        memanjang ? 'content-start overflow-y-auto' : ''
      }`}
      style={{
        gridTemplateColumns: `repeat(${kolom}, minmax(0, 1fr))`,
        // Baris sebanyak yang BENAR-BENAR terisi, bukan sebanyak baris tata
        // letaknya. Empat kamera pada 3×3 memakai dua baris; memaksa tiga baris
        // menyisakan satu baris kosong yang terlihat seperti bug.
        //
        // Di ponsel tegak tinggi baris datang dari LEBAR tile (16:9), bukan dari
        // pembagian tinggi kotak — itu yang menghabiskan batang hitam kiri-kanan.
        // Dihitung, bukan `auto`: alasannya panjang, ada di `tinggiBaris`.
        gridTemplateRows: `repeat(${Math.max(1, Math.ceil(halaman.length / kolom))}, ${
          tinggiBaris > 0 ? `${tinggiBaris}px` : 'minmax(0, 1fr)'
        })`,
      }}
    >
      {halaman.map((camera, i) => (
        <CameraTile
          key={camera.id}
          camera={camera}
          // Hanya tile di halaman aktif yang streaming (§D3.2). Tab yang
          // tersembunyi juga berhenti — tidak ada yang melihatnya.
          streaming={tabTerlihat && fullscreenId === null}
          selected={aksi && i === selected}
          onSelect={
            aksi
              ? () => {
                  jedaSlideshow();
                  setSelected(i);
                }
              : undefined
          }
          onFullscreen={
            aksi
              ? () => {
                  jedaSlideshow();
                  setFullscreenId(camera.id);
                }
              : undefined
          }
          /**
           * Layar penuh: seluruh tile jadi jalan pintas ke kamera itu (§G8.2,
           * 23 Agu 2026). Bukan ikon pojok — pojok kanan atas layar sudah
           * dipakai tombol "Stop (Esc)", dan pada 1×1 atau 2×2 keduanya
           * bertumpuk.
           *
           * **Berlaku juga selama slideshow berjalan** (25 Agustus 2026,
           * diminta lapangan). Sampai revisi ini hanya mode manual dan
           * slideshow yang dijeda yang tilenya bisa dibuka, dengan alasan
           * §G7 no.5: di TV pos satpam salah pencet berarti layar pantau
           * ditinggalkan. Yang membatalkan alasan itu bukan modenya, melainkan
           * kenyataan bahwa ada tangan di tetikus — dan tangan itu ada justru
           * saat sesuatu yang mencurigakan lewat. Dua mode yang tampak sama
           * persis tapi salah satunya tidak menanggapi klik terbaca sebagai
           * aplikasi yang macet, bukan sebagai perlindungan.
           *
           * Kliknya menjeda slideshow lebih dulu (§G7 no.6: interaksi apa pun
           * menjeda): tanpa itu, menutup kamera akan mendaratkan orangnya di
           * halaman yang sudah berpindah dua kali selama dia menonton.
           */
          onTileClick={
            !aksi
              ? () => {
                  setJalan(false);
                  setFullscreenId(camera.id);
                }
              : undefined
          }
          /**
           * Pra-panas HD ikut mati selama slideshow BERJALAN (§D3.6): satu
           * worker HD = 9,7% core, dan penunjuk yang kebetulan tergeletak di
           * atas grid yang berpindah sendiri akan menyalakannya untuk kamera
           * yang tidak dilihat siapa pun.
           */
          praPanas={!(slideshow && jalan)}
          isiLebar={memanjang}
        />
      ))}
    </div>
  );

  /**
   * Slideshow berjalan sebagai gridview **layar penuh** (§G8.1).
   *
   * Bukan di dalam panel dashboard: slideshow dipakai di TV pos satpam yang
   * tidak punya operator, dan di sana sidebar, header, serta bilah kendali
   * hanya memakan layar. Ini pola aplikasi DVR — dan alasan yang sama dengan
   * §G7 no.1, cuma dibawa sampai ujungnya.
   */
  if (slideshow && terlihat.length > 0) {
    return (
      /**
       * Penunjuk tetikus ikut tidur bersama kendalinya (23 Agu 2026).
       *
       * Kendalinya sendiri sudah menghilang sejak Batch B dan terukur benar —
       * 4 detik, lalu bangun lagi oleh gerak/sentuh/tombol apa pun. Yang masih
       * tertinggal di layar TV pos satpam adalah **kursornya**: panah putih
       * yang membeku di tengah gambar semalaman, persis di layar yang seluruh
       * gunanya menampilkan gambar. `pointermove` sudah jadi pemicu bangun,
       * jadi ia muncul lagi pada gerakan pertama — tidak ada yang bisa
       * kehilangan tetikusnya karena ini.
       */
      <div
        {...geserHalaman}
        /* Sapuan menurun di sini bukan tarik-untuk-muat-ulang: tidak ada yang
           digulung, dan muat ulang justru membongkar layar penuhnya sekalian
           (lihat `lib/muatUlangAndroid.ts`). */
        data-tanpa-muat-ulang=""
        /* `touch-none`: sapuan mendatar harus sampai ke penangan di atas alih-
           alih diambil browser lebih dulu untuk gestur "kembali" atau pantulan
           overscroll. Tidak ada yang menggulung di layar ini, jadi tidak ada
           yang hilang karenanya. */
        className={`fixed inset-0 z-40 flex touch-none flex-col bg-black ${
          kendaliTampak ? '' : 'cursor-none'
        }`}
      >
        {gridEl(false)}

        {/* Keluar dari slideshow — tetap di pojok kanan atas, tempat yang sama
            dengan tombol tutup layar penuh per-tile (§G7 no.5). */}
        <button
          type="button"
          onClick={() => jedaSlideshow()}
          className="absolute right-3 top-3 z-10 flex min-h-tap items-center rounded-md bg-black/50 px-4 text-sm text-white ring-1 ring-white/20 transition-colors hover:bg-black/80"
        >
          {t('Stop (Esc)')}
        </button>

        {/* Pemilih tata letak — muncul saat ada tanda kehidupan, hilang sendiri
            sesudah 4 detik didiamkan (§G8.2).

            Mengganti grid dari sini **tidak** menyentuh jeda/lanjut dan tidak
            membongkar layar penuhnya: yang berubah cuma berapa tile yang
            tampil. Sebelum ini satu-satunya jalan adalah keluar dulu ke
            dashboard, ganti, lalu masuk lagi — dan masuk lagi selalu memulai
            dari halaman pertama dengan hitungan penuh, jadi operator kehilangan
            tempatnya setiap kali.

            Dilepas dari DOM saat tersembunyi, bukan disamarkan dengan
            `opacity-0`: tombol tembus pandang tetap bisa dihinggapi Tab, dan
            fokus yang mendarat di sesuatu yang tak terlihat adalah jebakan
            papan ketik. */}
        {kendaliTampak && (
          <div className="pointer-events-none absolute inset-x-0 bottom-[5.5rem] z-10 flex justify-center">
            <div
              ref={grupGridRef}
              role="group"
              aria-label={t('Grid layout')}
              onPointerEnter={bangunkanKendali}
              onFocusCapture={bangunkanKendali}
              className="animate-muncul pointer-events-auto flex items-center gap-1 rounded-full bg-black/60 p-1 text-white ring-1 ring-white/20 backdrop-blur-sm"
            >
              {pilihanTampil.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => {
                    // Sekali dipilih tangan, tata letaknya tidak pernah
                    // diutak-atik lagi oleh aturan bawaan (§G8) — sama seperti
                    // tombol yang ada di bilah dashboard.
                    layoutDipilihUser.current = true;
                    setLayout(n);
                    // Disimpan ke akun, sama seperti tombol di bilah dashboard:
                    // tata letak yang dipilih di TV tidak perlu dipilih ulang
                    // besok pagi.
                    savePrefs({ gridLayout: n });
                    bangunkanKendali();
                  }}
                  aria-pressed={layout === n}
                  className={`min-h-tap rounded-full px-3.5 text-sm tabular-nums transition-colors ${
                    layout === n ? 'bg-white/25 font-semibold' : 'hover:bg-white/15'
                  }`}
                >
                  {n.replace('x', '×')}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Kendali transport (§G8.2).

            Dua bentuk, karena mode layar penuhnya dua (24 Agustus 2026):

            - **Slideshow**: mundur · jeda/lanjut · maju, plus hitung mundur dan
              posisi halaman — tidak berubah.
            - **Layar penuh manual**: hanya `‹` dan `›`. Diminta lapangan.
              Tidak ada yang berjalan di mode ini, jadi tidak ada yang perlu
              dijeda; tombol jeda di sana cuma tombol yang tidak mengerjakan
              apa-apa, dan hitung mundur yang membeku di angka yang sama
              terbaca seperti aplikasi yang menggantung.

            Di bawah-tengah, bukan menyebar di pojok-pojok seperti Lensa
            produksi: kelompok yang berdekatan terbaca sebagai satu kendali, dan
            bawah-tengah adalah tempat yang tidak menutupi apa pun pada tata
            letak 2×2 — pojok-pojoknya justru tempat ikon tile berada.

            Ikut menyembunyikan diri bersama pemilih tata letak (23 Agu 2026).
            Sebelumnya ia menetap, dan hasilnya sebuah pil hitam yang menutupi
            bawah-tengah gambar sepanjang malam di layar yang justru disediakan
            untuk gambar. Yang menggantikan jaminan "selalu ada" adalah pemicu
            bangunnya: gerak, sentuhan, dan tombol apa pun memanggilnya kembali
            dalam satu tindakan.

            Dilepas dari DOM saat tersembunyi, bukan disamarkan `opacity-0` —
            alasan yang sama dengan pemilih tata letak: tombol tembus pandang
            tetap bisa dihinggapi Tab. Latarnya gelap pekat supaya tetap
            terbaca di atas adegan terang. */}
        {kendaliTampak && (!manual || pageCount > 1) && (
          <div className="pointer-events-none absolute inset-x-0 bottom-4 z-10 flex justify-center">
            <div
              ref={grupTransportRef}
              onPointerEnter={bangunkanKendali}
              onFocusCapture={bangunkanKendali}
              className={`animate-muncul pointer-events-auto flex items-center gap-1 rounded-full bg-black/60 p-1 text-white ring-1 ring-white/20 backdrop-blur-sm ${
                manual ? '' : 'pr-3'
              }`}
              role="group"
              aria-label={manual ? t('Page controls') : t('Slideshow controls')}
            >
              <TombolSlideshow
                onClick={() => pindahHalaman(-1)}
                label={t('Previous page')}
                garis={manual}
              >
                {manual ? (
                  <path d="M14.5 6l-5 6 5 6" />
                ) : (
                  <path d="M18 5.5a.8.8 0 0 0-1.24-.66l-8 5.14V5.5a.8.8 0 0 0-1.24-.66l-7 4.5a.8.8 0 0 0 0 1.32l7 4.5A.8.8 0 0 0 8.76 14.5V9.52l8 5.14A.8.8 0 0 0 18 14.5z" />
                )}
              </TombolSlideshow>

              {!manual && (
                <button
                  type="button"
                  onClick={() => setJalan((v) => !v)}
                  aria-pressed={!jalan}
                  title={jalan ? t('Pause slideshow') : t('Resume slideshow')}
                  aria-label={jalan ? t('Pause slideshow') : t('Resume slideshow')}
                  className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15 transition-[background-color,scale] duration-150 hover:bg-white/25 active:scale-[0.94] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                    {jalan ? (
                      <>
                        <rect x="6.5" y="5" width="3.6" height="14" rx="1" />
                        <rect x="13.9" y="5" width="3.6" height="14" rx="1" />
                      </>
                    ) : (
                      <path d="M9 5.14a1 1 0 0 1 1.53-.85l9.72 6.86a1 1 0 0 1 0 1.7l-9.72 6.86A1 1 0 0 1 9 18.86z" />
                    )}
                  </svg>
                </button>
              )}

              <TombolSlideshow
                onClick={() => pindahHalaman(1)}
                label={t('Next page')}
                garis={manual}
              >
                {manual ? (
                  <path d="M9.5 6l5 6-5 6" />
                ) : (
                  <path d="M6 5.5a.8.8 0 0 1 1.24-.66l8 5.14V5.5a.8.8 0 0 1 1.24-.66l7 4.5a.8.8 0 0 1 0 1.32l-7 4.5A.8.8 0 0 1 15.24 14.5V9.52l-8 5.14A.8.8 0 0 1 6 14.5z" />
                )}
              </TombolSlideshow>

              {/* Hitung mundur terlihat selama kendalinya terlihat (§G7 no.6).
                  Ia ikut tidur bersama kelompoknya sekarang: saat seluruh
                  kendali tidur yang diminta memang layar yang bersih, dan satu
                  tindakan apa pun mengembalikannya. Saat dijeda ia diganti kata
                  "Paused" — angka yang membeku terbaca seperti aplikasi yang
                  menggantung, bukan seperti jeda yang disengaja. */}
              {!manual && (
                <>
                  <span className="ml-1 min-w-[5.5rem] text-center text-sm tabular-nums">
                    {jalan ? (
                      <>
                        <span
                          className="mr-1.5 inline-block h-2 w-2 rounded-full bg-red-600 align-middle motion-safe:animate-rec"
                          aria-hidden="true"
                        />
                        {countdown}s
                      </>
                    ) : (
                      t('Paused')
                    )}
                  </span>
                  <span className="text-xs text-white/70">
                    {pageAman + 1}/{pageCount}
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        {fullscreenCamera && (
          <FullscreenViewer
            camera={fullscreenCamera}
            /* Layar penuh browser sudah dipegang mode ini — viewer menumpang,
               tidak memintanya sendiri (§G8.2, 25 Agustus 2026). */
            bersarang
            onClose={() => setFullscreenId(null)}
            onPrev={terlihat.length > 1 ? () => geserFullscreen(-1) : undefined}
            onNext={terlihat.length > 1 ? () => geserFullscreen(1) : undefined}
            posisi={{ index: Math.max(0, idxFullscreen), total: terlihat.length }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Wall Status (§G14) — satu bilah ±32px, dan TIDAK dirender sama sekali
          di layar penuh, slideshow, maupun kiosk (§G8.1). Di TV pos satpam tidak
          ada yang membaca persentase, dan tidak ada yang bisa mengkliknya;
          menyisakan 32px di sana berarti membayar tinggi gambar untuk baris
          yang tidak pernah dibaca. */}
      {lebar && !slideshow && fullscreenId === null && <WallStatus />}

      {/* Chrome sengaja setipis mungkin — video yang jadi raja (§G7 no.1). */}
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
        {/* Penanda monitor (§F9). Halaman ini terlihat persis sama dengan
            dashboard penuh, jadi tanpa penanda tidak ada cara membedakan
            "monitor berisi empat kamera" dari "dashboard yang tinggal empat
            kameranya" — dan yang kedua terbaca sebagai kamera yang hilang.
            Tanda silangnya jalan keluar: satu klik kembali ke seluruh kamera. */}
        {monitor && (
          <span className="flex min-h-tap items-center gap-1.5 rounded-md border border-line bg-subtle px-2.5 text-sm">
            <span className="max-w-[12rem] truncate font-medium">{monitor.name}</span>
            <Link
              to="/dashboard"
              aria-label={t('Leave monitor {nama}', { nama: monitor.name })}
              className="rounded p-1 text-muted hover:bg-surface"
            >
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </Link>
          </span>
        )}

        <input
          ref={searchRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          /* Sama dengan /cameras: viewer tidak menerima `host` (§F6). */
          placeholder={isStaff(user) ? t('Search Name, Host or Path') : t('Search Name or Path')}
          aria-label={t('Search cameras')}
          className="min-h-tap w-full rounded-md border border-line bg-surface px-3 text-sm sm:w-60"
        />

        {/* Satu kendali tersegmen, bukan empat kotak berdiri sendiri: pilihan
            tata letak itu satu keputusan, dan bingkai tunggal yang mengatakannya.
            Isian aktif tetap `bg-subtle` + teks aksen — putih di atas
            accent-solid baru aman pada ≥16px (§G2), sedangkan label ini 14px. */}
        <div
          className="flex items-center rounded-md border border-line p-0.5"
          role="group"
          aria-label="Grid layout"
        >
          {pilihanTampil.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => {
                layoutDipilihUser.current = true;
                setLayout(n);
                jedaSlideshow();
                // Disimpan ke akun supaya tidak perlu dipilih ulang tiap login.
                savePrefs({ gridLayout: n });
              }}
              aria-pressed={layout === n}
              className={`min-h-tap min-w-tap rounded px-2 text-sm transition-[background-color,scale] duration-150 active:scale-[0.96] ${
                layout === n ? 'bg-subtle font-semibold text-accent-text' : 'hover:bg-subtle'
              }`}
            >
              {n.replace('x', '×')}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          {/* Layar penuh manual dan Slideshow — ikon, bukan tombol berlabel
              kata (24 Agustus 2026).

              Yang dibuang bersama katanya adalah baris kedua di bilah ini:
              "Fullscreen" + "Slideshow" + kalimat posisi + "Previous"/"Next"
              tidak pernah muat dalam satu baris, dan baris kedua memakan tinggi
              grid — yang justru satu-satunya alasan halaman ini ada (§G7 no.1).
              Namanya tetap terbaca lewat gelembung keterangan dan `aria-label`,
              jadi tidak ada yang hilang selain lebarnya.

              Layar penuh manual (§G8.2, 23 Agu 2026) diminta lapangan sebagai
              mode ketiga di antara pilihan grid dan Slideshow: *"sebenernya
              kayak slideshow, tapi manual. Ini kepake kalo orang gak mau
              pindah-pindah halaman dari halaman tersebut, daripada harus nunggu
              slideshow."* Ia layar penuh yang sama dengan slideshow, masuk
              dalam keadaan berhenti dan tanpa kendali jeda/lanjut. */}
          <TombolIkon
            label={t('Fullscreen')}
            nama="fullscreen"
            onClick={() => {
              setManual(true);
              setJalan(false);
              setSlideshow(true);
            }}
          />

          <TombolIkon
            label={t('Slideshow')}
            nama="slideshow"
            onClick={() => {
              setManual(false);
              setJalan(true);
              setSlideshow(true);
            }}
          />

          <select
            value={interval}
            onChange={(e) => {
              setIntervalS(Number(e.target.value));
              setCountdown(Number(e.target.value));
            }}
            aria-label={t('Slideshow interval')}
            className="min-h-tap rounded-md border border-line bg-surface px-2 text-sm"
          >
            {INTERVALS.map((i) => (
              <option key={i.s} value={i.s}>
                {t(i.label)}
              </option>
            ))}
          </select>
        </div>

        {/* Paging bernomor `« ‹ 1 2 3 4 › »` (§G8, 24 Agustus 2026).

            Menggantikan sepasang tombol berlabel kata plus kalimat posisi
            lengkap ("Page 2 of 5 — cameras 21–40 of 96") yang duduk di
            sebelahnya. Kalimat itu yang membuat bilah ini patah jadi dua baris,
            dan yang dikatakannya — halaman mana yang sedang dibuka, dari berapa
            — sudah dikatakan nomor-nomornya sendiri, tanpa memakan satu baris
            penuh. Loncat ke halaman tertentu jadi ikut bisa; sebelum ini
            halaman 5 dari 5 berarti menekan Next empat kali. */}
        <NomorHalaman
          className="ml-auto"
          page={pageAman}
          pageCount={pageCount}
          onPage={(p) => {
            jedaSlideshow();
            setPage(p);
          }}
        />
      </div>

      {terlihat.length === 0 ? (
        /* Kondisi kosong punya desain sendiri, bukan halaman putih (§G7 no.7). */
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
          <p className="text-lg">
            {query ? t('No matching cameras') : t('No cameras to show yet')}
          </p>
          <p className="text-sm text-muted">
            {query
              ? t('Nothing matches “{cari}”. Try a different search.', { cari: query })
              : t('Ask an administrator to add cameras to your dashboard.')}
          </p>
        </div>
      ) : (
        gridEl(true)
      )}

      {fullscreenCamera && (
        <FullscreenViewer
          camera={fullscreenCamera}
          onClose={() => setFullscreenId(null)}
          onPrev={terlihat.length > 1 ? () => geserFullscreen(-1) : undefined}
          onNext={terlihat.length > 1 ? () => geserFullscreen(1) : undefined}
          posisi={{ index: Math.max(0, idxFullscreen), total: terlihat.length }}
        />
      )}
    </div>
  );
}

/**
 * Tombol mundur/maju di kendali layar penuh (§G8.2).
 *
 * `garis` menggambar isinya sebagai goresan, bukan bidang penuh: mode manual
 * memakai chevron `‹`/`›` — panah polos "pindah halaman" — sedangkan slideshow
 * memakai ikon lompat-lagu yang berbidang penuh, karena di sana memang ada
 * sesuatu yang sedang berjalan dan dilompati.
 */
function TombolSlideshow({
  onClick,
  label,
  garis = false,
  children,
}: {
  onClick: () => void;
  label: string;
  garis?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="flex h-11 w-11 items-center justify-center rounded-full transition-[background-color,scale] duration-150 hover:bg-white/20 active:scale-[0.94] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
    >
      <svg
        viewBox="0 0 24 24"
        className="h-5 w-5"
        fill={garis ? 'none' : 'currentColor'}
        stroke={garis ? 'currentColor' : undefined}
        strokeWidth={garis ? 2 : undefined}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {children}
      </svg>
    </button>
  );
}

function bacaDashboard(kunci: string | null): string | null {
  if (!kunci) return null;
  try {
    return localStorage.getItem(kunci);
  } catch {
    return null;
  }
}

function ingatDashboard(kunci: string | null, monitorId: string | null): void {
  if (!kunci) return;
  try {
    if (monitorId) localStorage.setItem(kunci, monitorId);
    else localStorage.removeItem(kunci);
  } catch {
    // Mode privat / storage dimatikan — cukup tidak diingat.
  }
}
